import React, { useState } from "react";
import { formatNumber } from "../../utils/formatter";

interface PerformanceMetricsData {
  group: string;
  onHandQty: number;
  turns: number;
  nonStockQty: number;
  devaluedQty: number;
  grossProfitPct: number;
  gmroi: number;
  level?: string;
  [key: string]: any;
}

interface PerformanceMetricsTableProps {
  data: any;
  totalRowData: any;
  selectedRow:any;
  updateSelectedRow:any;
  updateSelectedRowLevel:any;
  loading: boolean;
  onSelectCategory?: (group: PerformanceMetricsData) => void;
}

const PerformanceMetricsTable: React.FC<PerformanceMetricsTableProps> = ({ data, totalRowData , loading, onSelectCategory, selectedRow, updateSelectedRow, updateSelectedRowLevel }) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // Handle row click to set or clear the selected row
  const handleRowClick = (
    item: PerformanceMetricsData, 
    hierarchyLevel: number,
    isSelected: boolean
  ) => {
    if (isSelected) {
      // If already selected, deselect it
      updateSelectedRow(null);
      updateSelectedRowLevel(null);
      // Send empty object to parent if callback exists
      if (onSelectCategory) {
        onSelectCategory({} as PerformanceMetricsData);
      }
    } else {
      // If not selected, select it
      updateSelectedRow(item.group);
      updateSelectedRowLevel(item.viewLevel || '')
      if (onSelectCategory) {
        const levelMap = ["L1", "L2", "L3", "L4", "L5"];
        const categoryLevel = levelMap[Math.min(hierarchyLevel, 4)]; 
        
        const performanceMetricsData = { 
          ...item,
          level: categoryLevel 
        };
        
        onSelectCategory(performanceMetricsData);
      }
    }
  };

  // Toggle row expansion ONLY when the expand/collapse icon is clicked
  const toggleExpand = (group: string, event: React.MouseEvent) => {
    // This is important - prevent the event from bubbling up to the row click handler
    event.stopPropagation();
    
    setExpandedRows(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const renderCategories = (categories: any[], level: number = 0): React.ReactNode => {
    if (!categories || !Array.isArray(categories) || categories.length === 0) {
      return null;
    }
  
    return categories.map((item, index) => {
      if (!item) return null;
      
      const group =  item.group;
      const onHandQty = typeof item.onHandQty === 'number' ? item.onHandQty : 0;
      const turns = typeof item.turns === 'number' ? item.turns : 0;
      const nonStockQty = typeof item.nonStockQty === 'number' ? item.nonStockQty : 0;
      const devaluedQty = typeof item.devaluedQty === 'number' ? item.devaluedQty : 0;
      const grossProfitPct = typeof item.grossProfitPct === 'number' ? item.grossProfitPct : 0;
      const gmroi = typeof item.gmroi === 'number' ? item.gmroi : 0;
      const isExpanded = !!expandedRows[group];
      const isSelected = (selectedRow === group);
      // Determine if this item has children
      const childCategories = [];
      if (item.children && Array.isArray(item.children)) {
        childCategories.push(...item.children);
      }
      if (item.items && Array.isArray(item.items)) childCategories.push(...item.items);
      
      const hasChildCategories = childCategories.length > 0;
      
      const rowBgClass = index % 2 === 0 ? 'bg-white' : 'bg-[#f4f9f2]';
      
      return (
        <React.Fragment key={group}>
          <tr 
            className={`border-t border-[#9C9C9B] cursor-pointer ${selectedRow === group ? 'bg-[#d9e8d4]' : rowBgClass}`}
            onClick={() => handleRowClick(item, level, isSelected)}
          >
            <td className="border-r p-3 text-black table-cell-title">
              <div className="flex items-center">
                <div style={{ marginLeft: `${level * 20}px` }} className="flex items-center">
                  {hasChildCategories ? (
                    <span 
                      className="mr-2 inline-block cursor-pointer text-black"
                      onClick={(e) => toggleExpand(group, e)}
                    >
                      {isExpanded ? (
                        <svg height="3.5vh" fill="black" viewBox="0 0 24 24" width="3.5vh" xmlns="http://www.w3.org/2000/svg">
                          <path className="heroicon-ui" d="M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm4-8a1 1 0 0 1-1 1H9a1 1 0 0 1 0-2h6a1 1 0 0 1 1 1z"/>
                        </svg>
                      ) : (
                        <svg width="3.5vh" height="3.5vh" fill="black" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                          <path fill="black" d="M352 480h320a32 32 0 1 1 0 64H352a32 32 0 0 1 0-64z"></path>
                          <path fill="black" d="M480 672V352a32 32 0 1 1 64 0v320a32 32 0 0 1-64 0z"></path>
                          <path fill="black" d="M512 896a384 384 0 1 0 0-768 384 384 0 0 0 0 768zm0 64a448 448 0 1 1 0-896 448 448 0 0 1 0 896z"></path>
                        </svg>                     
                      )}
                    </span>
                  ) : (
                    <span className="mr-2 inline-block text-black text-xl">&nbsp;</span>
                  )}
                  <span>{group}</span>
                </div>
              </div>
            </td>
            <td className="border-r p-3 text-right text-black table-cell-title">{formatNumber(onHandQty)}</td>
            <td className="border-r p-3 text-right text-black table-cell-title">{formatNumber((turns || 0)?.toFixed(2))}</td>
            <td className="border-r p-3 text-right text-black table-cell-title">{formatNumber((nonStockQty || 0)?.toFixed(2))}</td>
            <td className="border-r p-3 text-right text-black table-cell-title">{formatNumber((devaluedQty || 0)?.toFixed(2))}</td>
            <td className="border-r p-3 text-right text-black table-cell-title">{formatNumber((grossProfitPct || 0)?.toFixed(2))}%</td>
            <td className="border-r p-3 text-right text-black table-cell-title">{formatNumber((gmroi || 0)?.toFixed(2))}</td>
          </tr>
          
          {isExpanded && hasChildCategories && (
            renderCategories(childCategories, level + 1)
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="w-full max-h-[75vh] overflow-x-auto overflow-y-auto flex flex-col bg-black rounded-lg shadow-sm border-gray-200">
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse">
          <thead className="bg-white">
            <tr className="border-b-[3px] border-[#26890d]">
              <th className="border-r p-3 text-center font-bold text-black w-[300px] font-semibold table-header-title "></th>
              <th className="border-r p-3 text-center font-bold text-black font-semibold table-header-title">{`OnHand (Qty)`}</th>
              <th className="border-r p-3 text-center font-bold text-black w-[300px] font-semibold table-header-title">Turns</th>
              <th className="border-r p-3 text-center font-bold text-black w-[300px] font-semibold table-header-title">{`Nonstock (Qty)`}</th>
              <th className="border-r p-3 text-center font-bold text-black w-[300px] font-semibold table-header-title">{`Devalued (Qty)`}</th>
              <th className="border-r p-3 text-center font-bold text-black w-[300px] font-semibold table-header-title">{`Gross Profit (%)`}</th>
              <th className="border-r p-3 text-center font-bold text-black w-[300px] font-semibold table-header-title">GMROI</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {loading ? (
              <tr>
                <td colSpan={7} className="p-3 text-center text-black">Loading...</td>
              </tr>
            ) : data && data.length > 0 ? (
              renderCategories(data, 0)
            ) : (
              <tr>
                <td colSpan={7} className="p-3 text-center text-black">No data available</td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-white border-t border-[#9C9C9B]">
            <tr>
              <td className="border-r p-3 font-semibold text-black table-cell-title">Total</td>
              <td className="border-r p-3 text-right font-semibold text-black table-cell-title">{formatNumber((totalRowData.onHandQty || 0))}</td>
              <td className="border-r p-3 text-right font-semibold text-black table-cell-title">{formatNumber((totalRowData.turns || 0)?.toFixed(2))}</td>
              <td className="border-r p-3 text-right font-semibold text-black table-cell-title">{formatNumber((totalRowData.nonStockQty || 0)?.toFixed(2))}</td>
              <td className="border-r p-3 text-right font-semibold text-black table-cell-title">{formatNumber((totalRowData.devaluedQty || 0)?.toFixed(2))}</td>
              <td className="border-r p-3 text-right font-semibold text-black table-cell-title">{formatNumber((totalRowData.grossProfitPct || 0)?.toFixed(2))}</td>
              <td className="border-r p-3 text-right font-semibold text-black table-cell-title">{formatNumber((totalRowData.gmroi || 0)?.toFixed(2))}</td>

            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default PerformanceMetricsTable;
